import { useQuery } from "@tanstack/react-query";

import type { GameMode } from "./types";

export interface LivePrices {
  BCH: number;
  BTC: number;
  /** True if these are real fetched prices; false if fallback defaults. */
  live: boolean;
  /** When the prices were fetched (ms epoch), if live. */
  fetchedAt?: number;
}

/** Fallback prices if the network request fails (late-2026 ballpark). */
export const FALLBACK_PRICES: Record<GameMode, number> = {
  BCH: 250,
  BTC: 79000,
};

const CORS_PROXY = "https://proxy.shakespeare.diy/?url=";

/** Fetch a single coin's USD spot price from Coinbase's public API. */
async function fetchSpot(base: GameMode, signal: AbortSignal): Promise<number> {
  const target = `https://api.coinbase.com/v2/prices/${base}-USD/spot`;

  // Try the direct request first (Coinbase is CORS-enabled), then fall back
  // to the CORS proxy if the browser blocks it.
  const urls = [target, `${CORS_PROXY}${encodeURIComponent(target)}`];

  let lastErr: unknown;
  for (const url of urls) {
    try {
      const res = await fetch(url, { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data?: { amount?: string } };
      const amount = Number(json?.data?.amount);
      if (Number.isFinite(amount) && amount > 0) return amount;
      throw new Error("Invalid price payload");
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error("Price fetch failed");
}

/**
 * Fetch live BCH and BTC spot prices. Falls back to sensible defaults if the
 * request fails, so the game always works offline. Prices are cached for the
 * session (a game run should use a single stable price anchor).
 */
export function useLivePrices() {
  return useQuery<LivePrices>({
    queryKey: ["live-prices"],
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,
    retry: 1,
    queryFn: async (c) => {
      const [bch, btc] = await Promise.all([
        fetchSpot("BCH", c.signal),
        fetchSpot("BTC", c.signal),
      ]);
      return {
        BCH: bch,
        BTC: btc,
        live: true,
        fetchedAt: Date.now(),
      };
    },
    // If the query errors, surface the fallback via `select`-style default.
    // TanStack keeps `data` undefined on error, so consumers handle that.
  });
}
